import { useTranslation } from 'react-i18next';
import Card from '../shared/Card';
import {
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  SignalIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';
import { TaskRun } from '../../types';

export function summarizeRuns(runs: TaskRun[]): { total: number; errorCount: number } {
  return {
    total: runs.length,
    errorCount: runs.filter((r) => r.status === 'error').length,
  };
}

interface DashboardStatsProps {
  enabledOrgCount: number;
  stationCount: number;
  taskTotal: number;
  taskErrorCount: number;
  health?: {
    loading: boolean;
    error: string | null;
    status: 'ok' | 'degraded' | null;
    version?: string;
  };
}

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  color?: string;
}

function StatCard({ title, value, subtitle, icon: Icon, color }: StatCardProps) {
  return (
    <Card className="flex flex-col gap-2 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-caption text-muted uppercase tracking-wide">{title}</span>
          <span className={cn('text-number-display font-semibold text-ink tabular-nums', color)}>
            {value}
          </span>
          {subtitle ? <span className="text-caption text-muted">{subtitle}</span> : null}
        </div>
        <Icon className={cn('h-7 w-7 shrink-0 opacity-90', color)} />
      </div>
    </Card>
  );
}

export default function DashboardStats({
  enabledOrgCount,
  stationCount,
  taskTotal,
  taskErrorCount,
  health,
}: DashboardStatsProps) {
  const { t } = useTranslation();

  const healthCard = (() => {
    if (!health || health.loading) {
      return {
        value: health?.loading ? '…' : '—',
        subtitle: t('dashboard.healthChecking', { defaultValue: 'กำลังตรวจสอบ' }),
        color: 'text-muted' as const,
      };
    }
    if (health.error || !health.status) {
      return {
        value: t('dashboard.healthUnreachable', { defaultValue: 'ติดต่อไม่ได้' }),
        subtitle: health.error?.slice(0, 60) || t('dashboard.healthNoResponse', { defaultValue: 'ไม่มีการตอบกลับ' }),
        color: 'text-semantic-down' as const,
      };
    }
    if (health.status === 'ok') {
      return {
        value: t('layout.healthOk', { defaultValue: 'ปกติ' }),
        subtitle: health.version ? `API v${health.version}` : t('dashboard.healthOkSub', { defaultValue: 'บริการหลักพร้อม' }),
        color: 'text-semantic-up' as const,
      };
    }
    return {
      value: t('layout.healthDegraded', { defaultValue: 'เสื่อม' }),
      subtitle: t('dashboard.healthDegradedSub', { defaultValue: 'บางบริการมีปัญหา' }),
      color: 'text-accent-yellow' as const,
    };
  })();

  const taskSubtitle =
    taskErrorCount > 0
      ? t('dashboard.statsTasksErrors', { count: taskErrorCount, defaultValue: '{{count}} รายการผิดพลาด' })
      : t('dashboard.statsTasksOk', { defaultValue: 'ไม่มีข้อผิดพลาด' });

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatCard
        title={t('dashboard.statsOrgs', { defaultValue: 'องค์กรที่เปิดใช้' })}
        value={enabledOrgCount}
        icon={BuildingOffice2Icon}
        color="text-primary"
        subtitle={t('dashboard.statsOrgsSub', { defaultValue: 'ใน Agent Station' })}
      />
      <StatCard
        title={t('dashboard.statsStations')}
        value={stationCount}
        icon={BuildingOfficeIcon}
        color="text-primary"
        subtitle={t('dashboard.statsStationsSubAll', { defaultValue: 'จาก inventory' })}
      />
      <StatCard
        title={t('dashboard.statsTasks', { defaultValue: 'งานล่าสุด' })}
        value={taskTotal}
        icon={InboxIcon}
        color={taskErrorCount > 0 ? 'text-accent-yellow' : 'text-primary'}
        subtitle={taskSubtitle}
      />
      <StatCard
        title={t('dashboard.statsBackend')}
        value={healthCard.value}
        subtitle={healthCard.subtitle}
        icon={SignalIcon}
        color={healthCard.color}
      />
    </div>
  );
}
